import { useState } from 'react'

import { fehlertext } from '../../api/client'
import type { DiagnosisCheck } from '../../api/types'
import { useDiagnosis } from '../../hooks/queries'
import { useOnline } from '../../hooks/useOutbox'
import { useTexte } from '../../texte'

/**
 * The consistency checks after a migration (#131, 1.114.0) -- run before
 * the maintenance is switched off.
 *
 * **They only read.** A finding names table and id (and the trip where
 * there is one); what to do about it is a person's decision, and the places
 * that repair already exist (storage, cleanup). The checks themselves are
 * the server's register (`Diagnosis::CHECKS`); this card only has words for
 * them and shows an unknown one by its key.
 */
export function DiagnosisCard() {
  const texte = useTexte()
  const t = texte.betrieb.diagnosis
  const online = useOnline()
  const all = useDiagnosis()
  const one = useDiagnosis()
  // The answers so far, by key: "Erneut prüfen" replaces one row, not the list.
  const [checks, setChecks] = useState<DiagnosisCheck[]>([])

  function merge(fresh: DiagnosisCheck[]) {
    setChecks((old) => {
      if (old.length === 0) return fresh
      return old.map((row) => fresh.find((f) => f.key === row.key) ?? row)
    })
  }

  const busy = all.isPending || one.isPending
  const error = all.error ?? one.error

  return (
    <div className="card card--seite" data-diagnosis="">
      <p className="card__lead">{t.lead}</p>

      {error !== null && (
        <p className="notice" role="alert">
          {fehlertext(error)}
        </p>
      )}

      {checks.map((check) => (
        <div className="stack stack--eng" key={check.key} data-diagnosis-check={check.key} data-ok={check.ok ? 'yes' : 'no'} data-count={check.count}>
          <p className="dienst">
            <span className="dienst__name">{t.checks[check.key] ?? check.key}</span>
            <span className="dienst__zeilen">{check.ok ? t.ok : t.findings(check.count)}</span>
          </p>
          {check.examples.map((example) => (
            <p className="leise" key={example.ref} data-diagnosis-finding={example.ref}>
              {example.trip_title === null ? example.ref : t.findingInTrip(example.ref, example.trip_title)}
            </p>
          ))}
          {check.count > check.examples.length && (
            <p className="leise">{t.more(check.count - check.examples.length)}</p>
          )}
          <button
            type="button"
            className="button button--quiet button--klein"
            disabled={!online || busy}
            data-diagnosis-again={check.key}
            onClick={() => one.mutate(check.key, { onSuccess: (answer) => merge(answer.checks) })}
          >
            {t.again}
          </button>
        </div>
      ))}

      <button
        type="button"
        className="button button--outline button--schmal button--luft"
        disabled={!online || busy}
        data-diagnosis-all=""
        onClick={() => all.mutate(undefined, { onSuccess: (answer) => setChecks(answer.checks) })}
      >
        {busy ? t.running : t.all}
      </button>
    </div>
  )
}
